import { graphql, Link, useStaticQuery } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import React from "react"

import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/ui/table"

import Button from "@components/Button/Button"
import Layout from "@components/Layout/Layout"
import ProductHero from "@components/ProductHero/ProductHero"
import { cn } from "@utils/utils"

import AllPrecos from "@components/AllPrecos/AllPrecos"
import ContactUs from "@components/ContactUs/contactus"
import DocumentLink from "@components/DocumentLink/DocumentLink"

//Data import
import { walls } from "@components/AllPrecos/AllPrecos"

const GreenPrecastTemplate = ({ lang, texts }) => {
  const data = useStaticQuery(graphql`
    {
      product: file(
        name: { eq: "mur_precoffre_TH_Green" }
        sourceInstanceName: { eq: "images" }
      ) {
        ...Constrained
      }
      greenbuilding: file(
        name: { eq: "green-building" }
        sourceInstanceName: { eq: "images" }
      ) {
        ...Constrained
      }
      coupe: file(
        name: { eq: "coupe_de_principe_mur_bas_carbone" }
        sourceInstanceName: { eq: "images" }
      ) {
        ...Constrained
      }
      de_coupe: file(
        name: { eq: "coupe_de_principe_mur-DE" }
        sourceInstanceName: { eq: "images" }
      ) {
        ...Constrained
      }
      photocoupe: file(
        name: { eq: "photo-coupe-de-principe" }
        sourceInstanceName: { eq: "images" }
      ) {
        ...Constrained
      }
      leaf: file(
        name: { eq: "feuille" }
        sourceInstanceName: { eq: "images" }
      ) {
        ...Constrained
      }
      co2: file(
        name: { eq: "baisse_emissions_CO2" }
        sourceInstanceName: { eq: "images" }
      ) {
        ...Constrained
      }
      earth: file(
        name: { eq: "main_planete_verte" }
        sourceInstanceName: { eq: "images" }
      ) {
        ...Constrained
      }
    }
  `)
  return (
    <Layout lang={lang} dropdownId="greenwall">
      <ProductHero>
        <div className="flex max-w-full basis-1/3 flex-col justify-between gap-y-5 p-4 lg:p-8">
          <div className="mt-24 flex flex-col gap-y-4">
            <h1>{texts.title}</h1>
            {texts.subtitle && <h2 className="text-2xl">{texts.subtitle}</h2>}
            <p>{texts.copy}</p>
            <ul className="list-inside">
              {texts.pros.map((it, i) => (
                <li key={i} className="arrow-before">
                  {it.title}
                </li>
              ))}
            </ul>
          </div>
          <div className="flex flex-col">
            {texts.cta.map((it, i) => (
              <Button
                key={i}
                buttonString={it.title}
                buttonURL={it.slug}
                {...(i === 0 && { black: true })}
              />
            ))}
          </div>
        </div>
        <div className="grid basis-2/3 grid-cols-2 grid-rows-2">
          <div className="col-span-1 row-span-1 bg-gray-100 p-4">
            <GatsbyImage
              objectFit="contain"
              image={getImage(data.product)!}
              alt={texts.seo_title || ""}
              className="h-full"
            />
          </div>
          <GatsbyImage
            objectPosition="top"
            image={getImage(data.photocoupe)!}
            alt={texts.seo_subtitle || ""}
            className="col-start-2 row-span-1"
          />
          <GatsbyImage
            objectPosition="top"
            image={getImage(data.greenbuilding)!}
            alt={texts.title || ""}
            className="col-span-full row-start-2"
          />
        </div>
      </ProductHero>

      {/* Technical data table */}
      <div id="main" className="mb-7 flex flex-col lg:mb-0 lg:flex-row">
        <div className="basis-1/3 px-5 py-20 lg:border-t-[1px] lg:border-t-gray-200">
          <h2 className="mb-3 uppercase">{texts.variante.title[lang]}</h2>
          <div className="flex flex-col gap-y-2">
            {walls[lang].products.map((variant, i) => (
              <Link key={i} to={variant.url} className="text-black underline">
                {variant.name}
              </Link>
            ))}
          </div>
        </div>
        <div className="mx-auto max-w-4xl">
          <Table className="my-10 table-auto lg:my-14">
            <TableHeader className="text-center">
              <TableRow className="border-none">
                <TableHead>&nbsp;</TableHead>
                {texts.product_table.table_data.thead.map((th, i) => (
                  <TableHead
                    key={i}
                    className="py-2 text-center text-base font-bold uppercase text-black"
                  >
                    {th}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody className="space-y-10">
              {texts.product_table.table_data.tr.map((tr, i) => (
                <TableRow key={i}>
                  {tr.map((td, i) => (
                    <TableCell
                      key={i}
                      className={cn(
                        "mb-1 bg-fehr-500 p-2 text-white",
                        i > 0 && "text-center"
                      )}
                    >
                      {td.data}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
            {texts.product_table.asterix && (
              <TableCaption>{texts.product_table.asterix}</TableCaption>
            )}
          </Table>
        </div>
      </div>

      <section className="mx-auto max-w-4xl space-y-5 px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-xl grid-cols-3 gap-10">
          {["co2", "leaf", "earth"].map((it, idx) => (
            <GatsbyImage
              key={idx}
              image={getImage(data[it])}
              alt={data[it].name}
              objectFit="contain"
            />
          ))}
        </div>
        {texts.summary.map((paragraph: string, i: number) => (
          <p key={i}>{paragraph}</p>
        ))}
      </section>
      <section className="mx-auto my-10 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 content-center gap-4 md:grid-cols-2">
          {texts.twocol.map((col, i) => (
            <GatsbyImage
              key={i}
              image={getImage(data[col.img])!}
              imgStyle={{ objectFit: "contain" }}
              alt={col.alt}
            />
          ))}
          <ul className="mx-auto flex flex-col justify-center gap-4 px-4 sm:px-6 lg:px-8">
            {texts.cards.map((card, i) => (
              <li key={i} className="arrow-before">
                {card}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Download Section */}
      {texts.downloads && (
        <section
          id="download"
          className="mx-auto my-20 max-w-7xl px-3 sm:px-6 lg:my-32 lg:px-8"
        >
          <h2 className="mb-16 text-center uppercase">
            {texts.downloads.title}
          </h2>
          <div>
            {texts.downloads.sections.map((section, i) => (
              <div key={i} className="mb-20">
                <h3 className="mb-8 p-2 font-extralight uppercase lg:p-7">
                  {section.title}
                </h3>
                <DocumentLink
                  key={i}
                  text={section.title}
                  slug={`${process.env.GATSBY_DOCUMENTATION_URL}/${section["slug"]}`}
                />
              </div>
            ))}
          </div>
        </section>
      )}
      <AllPrecos lang={lang} />
      <ContactUs lang={lang} />
    </Layout>
  )
}

export default GreenPrecastTemplate
